import type { SalesOrderLineItem, SalesOrderShipmentSummary } from '@erp/rpc';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { TroubleDataBadges } from '@/components/common/TroubleDataBadges';
import { formatDateOnlyString } from '@/utils/dateUtils';
import { T, Var } from 'gt-react';
import { LineItemProductInfo } from '@/components/line-item-product-info/LineItemProductInfo';
import { Currency, formatCurrency } from '@/components/common/Currency';

interface SalesOrderLineItemsProps {
  lineItems: SalesOrderLineItem[];
  currency?: string | null;
  shipment?: SalesOrderShipmentSummary | null;
}

export function SalesOrderLineItems({
  lineItems,
  currency,
  shipment,
}: SalesOrderLineItemsProps) {
  if (lineItems.length === 0) {
    return (
      <div className="p-4 text-center text-sm text-muted-foreground">
        <T>No line items found</T>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[60px]">#</TableHead>
          <TableHead>
            <T>Product</T>
          </TableHead>
          <TableHead className="text-right">
            <T>Qty</T>
          </TableHead>
          <TableHead className="text-right">
            <T>Sale Price</T>
          </TableHead>
          <TableHead className="text-right">
            <T>Supplier Cost</T>
          </TableHead>
          <TableHead className="text-right">
            <T>Total</T>
          </TableHead>
          <TableHead>
            <T>Delivery</T>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {lineItems.map((item, index) => (
          <TableRow key={item.id}>
            <TableCell className="font-mono text-muted-foreground">
              {item.lineItemNumber ?? index + 1}
            </TableCell>
            <TableCell>
              <div className="flex flex-col gap-1">
                <LineItemProductInfo
                  productId={item.productId}
                  productVariantId={item.productVariantId}
                  productName={item.productName}
                  productSku={item.productSku}
                  productVariantSku={item.productVariantSku}
                />
                {item.customerProductSku && (
                  <span className="text-xs text-muted-foreground">
                    <T>
                      Customer SKU: <Var>{item.customerProductSku}</Var>
                    </T>
                  </span>
                )}
              </div>
            </TableCell>
            <TableCell className="text-right">
              {item.supplierQuantity != null &&
              Number(item.supplierQuantity) !== Number(item.quantity) ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help underline decoration-dotted decoration-muted-foreground/40">
                      {item.supplierQuantity}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="font-medium">
                      <T>Finalized shipped quantity</T>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      <T>
                        From fulfillment records (AR_TRX_LINE.QNT) — SO-stage
                        qty: <Var>{item.quantity ?? '—'}</Var>
                      </T>
                      {shipment?.originLoadNumber && (
                        <>
                          {' — '}
                          <T>
                            Load #<Var>{shipment.originLoadNumber}</Var>
                          </T>
                        </>
                      )}
                    </p>
                  </TooltipContent>
                </Tooltip>
              ) : (
                (item.supplierQuantity ?? item.quantity ?? '-')
              )}
            </TableCell>
            <TableCell className="text-right">
              <Currency
                value={item.unitPrice}
                currency={currency ?? 'USD'}
                fallback="-"
              />
            </TableCell>
            <TableCell className="text-right">
              <div className="flex items-center justify-end gap-1">
                {item.supplierCostPrice ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help underline decoration-dotted decoration-muted-foreground/40">
                        {formatCurrency(item.supplierCostPrice, {
                          currency: currency ?? 'USD',
                        }) || '-'}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="font-medium">
                        <T>Finalized supplier cost</T>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        <T context="sales order line item supplier cost source">
                          From fulfillment records (AR_TRX_DETAIL)
                        </T>
                        {shipment?.originLoadNumber && (
                          <>
                            {' — '}
                            <T context="sales order line item load number">
                              Load #<Var>{shipment.originLoadNumber}</Var>
                            </T>
                          </>
                        )}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                ) : item.costPrice ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help text-muted-foreground underline decoration-dotted decoration-muted-foreground/40">
                        {formatCurrency(item.costPrice, {
                          currency: currency ?? 'USD',
                        }) || '-'}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="font-medium">
                        <T>Estimated cost</T>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        <T context="sales order line item estimated cost tooltip">
                          SO-stage estimate — not yet finalized
                        </T>
                      </p>
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
                <TroubleDataBadges
                  troubleData={item.troubleData}
                  legacyConsign={item.isConsignment ?? false}
                />
              </div>
            </TableCell>
            <TableCell className="text-right font-medium">
              <Currency
                value={item.lineTotal}
                currency={currency ?? 'USD'}
                fallback="-"
              />
            </TableCell>
            <TableCell>
              {item.rescheduledDate
                ? formatDateOnlyString(item.rescheduledDate)
                : '-'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
